import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const OUTDATED_LOGIC = ["Processos forçados", "Informação duplicada", "Limites do software"];
const NEW_LOGIC = ["Entender", "Desenhar", "Construir", "Evoluir"];

export const CinematicBreak = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  const firstX = useTransform(scrollYProgress, [0, 0.72], [0, -90]);
  const firstScale = useTransform(scrollYProgress, [0, 0.72], [1, 0.96]);
  const overlayClip = useTransform(
    scrollYProgress,
    [0.26, 0.76],
    ["inset(0 100% 0 0)", "inset(0 0% 0 0)"],
  );
  const overlayX = useTransform(scrollYProgress, [0.26, 0.76], [90, 0]);
  const wipePosition = useTransform(scrollYProgress, [0.26, 0.76], ["0%", "100%"]);
  const progressScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section ref={ref} id="mudanca-de-logica" data-header-theme="dark" className="relative h-[190svh] bg-[#050606] md:h-[205svh]" data-testid="cinematic-break">
      <div className="sticky top-0 h-[100svh] min-h-[640px] overflow-hidden bg-[#050606]">
        <div className="logic-grid absolute inset-0 opacity-65" aria-hidden="true" />
        <motion.div className="site-shell relative z-10 flex h-full flex-col pb-8 pt-[112px] md:pb-10 md:pt-[124px]" style={reduce ? undefined : { x: firstX, scale: firstScale }}>
          <div className="flex items-center border-b border-white/12 pb-5">
            <p className="eyebrow">Inverta a lógica</p>
          </div>

          <div className="flex flex-1 items-center py-8 md:py-12">
            <h2 className="display-title max-w-[1500px] text-[clamp(3.15rem,8.5vw,9.2rem)] leading-[.91] text-[#f1f3ec]">
              Sua empresa não foi feita para <span className="text-white/30">caber numa ferramenta.</span>
            </h2>
          </div>

          <div className="grid gap-6 border-t border-white/12 pt-6 lg:grid-cols-[.8fr_1.2fr] lg:items-end">
            <p className="max-w-xl text-sm leading-relaxed text-white/52 md:text-base">
              Quando a operação se adapta ao software, ela também herda limitações que nunca foram do negócio.
            </p>
            <div className="grid grid-cols-3 border-l border-white/10">
              {OUTDATED_LOGIC.map((item, index) => (
                <div key={item} className="border-r border-white/10 px-3 py-2 sm:px-5">
                  <p className="text-[8px] font-bold uppercase tracking-[.16em] text-white/22">0{index + 1}</p>
                  <p className="mt-1 text-[9px] leading-tight text-white/42 sm:text-xs">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div
          data-header-theme="light"
          className="absolute inset-0 z-20 overflow-hidden bg-lime-300 text-[#050606]"
          style={reduce ? { clipPath: "inset(0 0% 0 0)" } : { clipPath: overlayClip }}
        >
          <div className="logic-grid-dark absolute inset-0 opacity-65" aria-hidden="true" />
          <motion.div className="site-shell relative flex h-full flex-col pb-8 pt-[112px] md:pb-10 md:pt-[124px]" style={reduce ? undefined : { x: overlayX }}>
            <div className="flex items-center justify-between gap-6 border-b border-black/18 pb-5">
              <p className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[.2em] text-black/62 sm:text-xs">
                <span className="h-2 w-2 rounded-full bg-black" /> A resposta não é mais uma ferramenta
              </p>
              <p className="text-[9px] font-black uppercase tracking-[.2em] text-black/35">Clube Digital · Sob medida</p>
            </div>

            <div className="flex flex-1 items-center py-8 md:py-12">
              <h2 className="display-title max-w-[1500px] text-[clamp(3.15rem,8.5vw,9.2rem)] leading-[.91] text-[#050606]">
                A tecnologia é que precisa <span className="text-black/42">nascer da sua empresa.</span>
              </h2>
            </div>

            <div className="grid gap-6 border-t border-black/18 pt-6 lg:grid-cols-[.8fr_1.2fr] lg:items-end">
              <p className="max-w-xl text-sm font-medium leading-relaxed text-black/62 md:text-base">
                Primeiro entendemos a operação. Depois construímos exatamente o que ela precisa para avançar.
              </p>
              <div className="grid grid-cols-4 border-l border-black/16">
                {NEW_LOGIC.map((item, index) => (
                  <div key={item} className="border-r border-black/16 px-2 py-2 sm:px-5">
                    <p className="text-[8px] font-black uppercase tracking-[.16em] text-black/30">0{index + 1}</p>
                    <p className="mt-1 text-[9px] font-bold leading-tight text-black/70 sm:text-xs">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>

        {!reduce && (
          <motion.div className="pointer-events-none absolute inset-y-0 z-30 w-px bg-black/65 shadow-[0_0_30px_rgba(217,224,33,.32)]" style={{ left: wipePosition }} aria-hidden="true">
            <span className="absolute left-1/2 top-1/2 h-20 w-[5px] -translate-x-1/2 -translate-y-1/2 bg-black" />
          </motion.div>
        )}

        <div className="absolute bottom-0 left-0 z-40 h-[3px] w-full bg-white/10" aria-hidden="true">
          <motion.div className="h-full origin-left bg-[#00d2ff]" style={{ scaleX: progressScale }} />
        </div>
      </div>
    </section>
  );
};
